import {
  ReactNode,
  createContext,
  useCallback, useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AuthService from '../services/authService';

import useApiCall from '../hooks/useApiCall';
import useLocalState from '../hooks/useLocalState';
import { isLocal } from '../pathName';
import authService from '../services/authService';

interface AuthProviderProps {
  children: ReactNode;
}

export interface AppContextInterface {
  signed: boolean,
  token: string,
  user: {
    partnerId: string,
    partnerName: string,
    email: string,
    classPrice: number,
  }
  signOut: () => void,
  login: (email: string, password: string) => void,
  loginWithGoogle: (code: string) => void,
}

const AuthContext = createContext({});

export function useAppContext(): AppContextInterface | any {
  const context = useContext(AuthContext);

  return context;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [signed, setSigned] = useState(false);
  const [signedLocalStorage, setSignedLocalStorage] = useLocalState('validado');

  const [tokenJwt, setTokenJwt] = useState('');
  const [tokenLocalStorage, setTokenLocalStorage] = useLocalState('token');

  const [userData, setUserData] = useState({});
  const [userDataLocalStorage, setUserDataLocalStorage] = useLocalState('userData');

  const navigate = useNavigate();
  const { apiCall } = useApiCall();

  const login = useCallback(
    async (email: string, password: string) => {
      await apiCall({
        apiToCall: AuthService.auth,
        reqBody: JSON.stringify({
          email,
          password,
        }),
        actionAfterResponse: (apiResponse) => {
          const { user, token, mustChangePassword } = apiResponse;
          if (user?._id && token) {
            setTokenJwt(token);
            setTokenLocalStorage(token);
            if (mustChangePassword) {
              navigate(`/reset-password?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
              toast.info('Você deve alterar sua senha para continuar', { toastId: 'changePassword' });
              return;
            }

            setUserData({
              partnerId: user._id,
              partnerName: user.nome_fantasia,
              email,
              classPrice: user.valor,
              permission: user.permission,
            });
            setUserDataLocalStorage({
              partnerId: user._id,
              partnerName: user.nome_fantasia,
              email,
              classPrice: user.valor,
              permission: user.permission,
            });
            setSigned(true);
            setSignedLocalStorage(true);
            toast.success('Login realizado, bem vindo ao sistema administrativo dos parceiros Kiddlepass!', {
              toastId: 'loginSuccess',
            });
            return;
          }
          setSigned(false);
          setSignedLocalStorage(false);
          const errorMessage = 'Login/senha inválidos';
          toast.error(errorMessage, {
            toastId: 'loginFail',
          });
        },
        // catchMessage: 'Erro. Por favor, tente novamente',
        catchAction: () => {
          setSigned(false);
          setSignedLocalStorage(false);
        }
      });
    },
    [apiCall, navigate, setSignedLocalStorage, setTokenLocalStorage, setUserDataLocalStorage],
  );

  const loginWithGoogle = useCallback(async (code: string) => {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    await apiCall({
      apiToCall: authService.googleLogin,
      reqBody: JSON.stringify({
        code,
        redirectUri: isLocal ? 'http://localhost:3000' :`${protocol}//${hostname}`,
      }),
      actionAfterResponse: (apiResponse) => {
        const { user, token, email, mustChangePassword } = apiResponse;
        if (user?._id && token) {
          setTokenJwt(token);
          setTokenLocalStorage(token);

          if (mustChangePassword) {
            navigate('/reset-password?token=' + token);
            toast.info('Você deve alterar sua senha para continuar', { toastId: 'changePassword' });
            return;
          }

          setUserData({
            partnerId: user._id,
            partnerName: user.nome_fantasia,
            email,
            classPrice: user.valor,
            permission: user.permission,
          });
          setUserDataLocalStorage({
            partnerId: user._id,
            partnerName: user.nome_fantasia,
            email,
            classPrice: user.valor,
            permission: user.permission,
          });
          setSigned(true);
          setSignedLocalStorage(true);
          toast.success('Login realizado, bem vindo ao sistema administrativo dos parceiros Kiddlepass!', {
            toastId: 'loginSuccess',
          });
          return;
        }
        setSigned(false);
        setSignedLocalStorage(false);
        const errorMessage = 'Login/senha inválidos';
        toast.error(errorMessage, {
          toastId: 'loginFail',
        });
      },
      catchAction: () => {
        setSigned(false);
        setSignedLocalStorage(false);
      },
      // catchMessage: 'Erro, por favor, tente novamente.'
    });
  }, [apiCall, navigate, setSignedLocalStorage, setTokenLocalStorage, setUserDataLocalStorage]);

  const signOut = useCallback(() => {
    toast.error('Sessão encerrada, realize o login novamente', {
      position: 'bottom-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'dark',
      toastId: 'logout',
    });
    setSigned(false);
    setSignedLocalStorage(false);

    setTokenJwt('');
    setTokenLocalStorage('');

    setUserData({});
    setUserDataLocalStorage({});
    navigate('/');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSignedLocalStorage, setTokenLocalStorage, setUserDataLocalStorage]);

  const appData = useMemo((): AppContextInterface => ({
    signed: signedLocalStorage || signed,
    user: userDataLocalStorage || userData,
    token: tokenJwt || tokenLocalStorage,
    signOut,
    login,
    loginWithGoogle,
  }), [signedLocalStorage, signed, userDataLocalStorage, userData, tokenJwt, tokenLocalStorage, signOut, login, loginWithGoogle]);

  // const appData = useMemo((): AppContextInterface => ({
  //   signed: true,
  //   user: {
  //     partnerId: '65565d43f95b8ab954d59ce3',
  //     partnerName: 'Professora Daniela',
  //     email: 'ninaalmeidatakara@gmail.com',
  //     classPrice: 0,
  //   },
  //   token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI2NTU2NWQ0M2Y5NWI4YWI5NTRkNTljZTMiLCJlbnYiOiJob21vbG9nIn0.k2pCPZijDvVCeNu3zrYcelRkVNLdfxqP2LY2UBVvc9c',
  //   signOut,
  //   login,
  //   loginWithGoogle,
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }), [signedLocalStorage, signed, userDataLocalStorage, userData, tokenJwt, tokenLocalStorage, signOut, login]);

  return (
    <>
      <AuthContext.Provider value={appData}>
        {children}
      </AuthContext.Provider>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  );
}
